import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const CPWrapper = styled.div`
  width: 100%;
  @media (min-width: 768px) and (max-width: 1023px) {
    width: 12.68rem;
  }
  @media (min-width: 1023px) {
    width: 11.25rem;
  }
  & label {
    font-family: "neue-haas-grotesk-text";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 8px;
  }
  & select {
    font-family: "neue-haas-grotesk-text";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
  }
`;

function DropdownFilter({ label, optionValues, optionsHandler, disabled, statusValue }) {
  const { t } = useTranslation("casesTranslation");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const menuRef = useRef(null); // Ref for the menu container
  const labelId = useRef(`dropdown-label-${Math.random().toString(36).substring(2, 11)}`);
  const selectId = useRef(`dropdown-select-${Math.random().toString(36).substring(2, 11)}`);

  const translatedOptions = optionValues.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const selectedOption = translatedOptions.find((option) => option.value === statusValue);

  // Custom Option component with keyboard support for TalkBack
  const CustomOption = ({ innerProps, data, ...rest }) => {
    // Combined handler for keyboard, touch and click events
    const handleSelection = (e) => {
      // For keyboard events
      if (e.type === "keydown" && (e.key === "Enter" || e.key === " " || e.keyCode === 13 || e.keyCode === 32)) {
        e.preventDefault();
        handleOptionSelect(data);
      }

      // For touch events
      if (e.type === "touchend") {
        e.preventDefault();
        handleOptionSelect(data);
      }
    };

    return (
      <components.Option
        innerProps={{
          ...innerProps,
          onKeyDown: handleSelection,
          onTouchEnd: handleSelection,
          tabIndex: 0, // Make option focusable
          role: "option",
        }}
        data={data}
        {...rest}
      />
    );
  };

  CustomOption.propTypes = {
    innerProps: PropTypes.object,
    data: PropTypes.object.isRequired,
  };

  const handleMenuOpen = () => {
    setMenuIsOpen(true);
    //  Focus the first option when the menu opens
    //  Need to wait for the menu to render
    setTimeout(() => {
      if (menuRef.current) {
        const firstOption = menuRef.current.querySelector('[role="option"]');
        if (firstOption) {
          firstOption.focus();
        }
      }
    }, 0);
  };

  const handleMenuClose = () => {
    setMenuIsOpen(false);
  };

  const handleOptionSelect = (selectedOption) => {
    optionsHandler(selectedOption.value);
    //  Return focus to the select control after selection
    if (selectRef.current) {
      selectRef.current.focus();

      // Announce selection to screen readers
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", "assertive");
      announcement.setAttribute("role", "status");
      announcement.setAttribute(
        "style",
        "position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;"
      );
      announcement.textContent = `Selected ${selectedOption.label}`;
      document.body.appendChild(announcement);

      // Remove the announcement after it's read
      setTimeout(() => {
        if (announcement.parentNode) {
          document.body.removeChild(announcement);
        }
      }, 1000);
    }
  };

  const keyDownHandler = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      if (!menuIsOpen) {
        e.preventDefault(); // Prevent default action for Enter/Space
        handleMenuOpen(); // Open the menu on Enter/Space
      }
    }
  };

  // Handle keyboard events for accessibility (TalkBack)
  useEffect(() => {
    //  Update aria-expanded when menuIsOpen changes
    if (selectRef.current) {
      selectRef.current.inputRef.setAttribute("aria-expanded", menuIsOpen);
    }
  }, [menuIsOpen]);

  return (
    <CPWrapper>
      <label htmlFor={selectId.current} id={labelId.current}>
        {label}
      </label>
      <div style={{ marginTop: "0.5rem" }}>
        <Select
          ref={selectRef}
          inputId={selectId.current}
          menuIsOpen={menuIsOpen}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          onChange={handleOptionSelect}
          options={translatedOptions}
          value={selectedOption}
          isDisabled={disabled}
          onKeyDown={keyDownHandler}
          components={{ Option: CustomOption }}
          styles={{
            indicatorSeparator: (base, state) => ({ backgroundColor: "none" }),
            control: (base, state) => ({
              ...base,
              width: "100%",
              height: "2.75rem",
              border: state.isFocused ? "2px solid var(--feedback-information, #006FC1)" : "1px solid #d8dada",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontFamily: '"neue-haas-grotesk-text"',
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              backgroundColor: state.isFocused ? "var(--background-secondary, #F6F6F6)" : "#fff",
              flexWrap: "nowrap",
              boxShadow: "none",
              ariaExpanded: state.menuIsOpen, //  This might not be necessary, but just in case
              ariaHaspopup: "listbox",
              ariaLabelledby: labelId.current,
            }),
            menu: (base) => ({
              ...base,
              width: "100%",
              zIndex: 9999,
              backgroundColor: "#fff",
              border: "1px solid #d8dada",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              role: "listbox",
            }),
            menuList: (base) => ({
              ...base,
              ref: (ref) => {
                menuRef.current = ref;
              },
            }),
            option: (base, state) => {
              let backgroundColor = "transparent";
              if (state.isFocused) {
                backgroundColor = "#0000000D";
              } else if (state.isSelected) {
                backgroundColor = "var(--background-secondary, #F6F6F6)";
              }

              return {
                ...base,
                paddingTop: "14px",
                paddingRight: "16px",
                paddingBottom: "14px",
                paddingLeft: "16px",
                gap: "10px",
                fontSize: "0.875rem",
                fontWeight: 400,
                fontFamily: '"neue-haas-grotesk-text"',
                color: "#000",
                backgroundColor,
                cursor: "pointer",
                padding: "0.5rem",
                borderLeft: state.isSelected ? "4px solid #EE0000" : "4px solid transparent",
                "&:active": {
                  backgroundColor: "#0000001F",
                },
                role: "option",
                ariaSelected: state.isSelected,
              };
            },
          }}
          placeholder="Select Status"
          isSearchable={false}
          id={selectId.current}
          aria-label="Status" //  Consider if aria-labelledby is sufficient
        />
      </div>
    </CPWrapper>
  );
}

DropdownFilter.propTypes = {
  label: PropTypes.string.isRequired,
  optionValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  optionsHandler: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  statusValue: PropTypes.string,
};

export default DropdownFilter;
